import { useMemo, useState } from 'react'
import { IconSearch } from '@tabler/icons-react'
import { DATASET_MAP, DATASETS } from '../datasets'
import { SELECTOR_SECTIONS } from '../datasets/sections'
import { useAppStore } from '../store'

const DatasetBrowser = () => {
  const datasetId = useAppStore((state) => state.datasetId)
  const setDatasetId = useAppStore((state) => state.setDatasetId)
  const scope = useAppStore((state) => state.scope)
  const [filterText, setFilterText] = useState('')
  const [activeFacets, setActiveFacets] = useState<Set<string>>(new Set())
  const [tagsExpanded, setTagsExpanded] = useState(false)
  const TAGS_VISIBLE = 2

  // Deterministic dataset order (SELECTOR_SECTIONS first, unlisted at the end).
  const ordered = useMemo(() => {
    const ids: string[] = []
    for (const s of SELECTOR_SECTIONS) for (const id of s.datasetIds) ids.push(id)
    for (const d of DATASETS) if (!ids.includes(d.id)) ids.push(d.id)
    return ids
  }, [])

  // Union of every tag across all datasets — the facet-chip vocabulary.
  const facetVocab = useMemo(() => {
    const set = new Set<string>()
    for (const id of ordered) {
      for (const t of DATASET_MAP[id]?.tags ?? []) set.add(t)
    }
    return Array.from(set)
  }, [ordered])

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase()
    return ordered.filter((id) => {
      const c = DATASET_MAP[id]
      if (!c) return false
      // Text match
      const textOk =
        !q ||
        c.info?.toLowerCase().includes(q) ||
        c.sourceInfo?.toLowerCase().includes(q) ||
        id.toLowerCase().includes(q) ||
        (c.tags ?? []).some((t) => t.toLowerCase().includes(q))
      // Facet match — every selected facet must be present on the dataset.
      const tags = new Set(c.tags ?? [])
      const facetOk = Array.from(activeFacets).every((t) => tags.has(t))
      return textOk && facetOk
    })
  }, [ordered, filterText, activeFacets])

  const toggleFacet = (t: string) => {
    setActiveFacets((prev) => {
      const next = new Set(prev)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })
  }

  // In collection-scoped mode the picker is hidden.
  if (scope === 'collection') return null

  return (
    <div style={{ marginTop: 4 }}>
      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 8 }}>
        <IconSearch
          size={13}
          stroke={2}
          style={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--grey)',
            pointerEvents: 'none',
          }}
        />
        <input
          type="search"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder="Search collections"
          aria-label="Search collections"
          style={{
            width: '100%',
            padding: '6px 8px 6px 26px',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'var(--paper)',
            fontFamily: 'var(--card-sans)',
            fontSize: 12,
            color: 'var(--navy)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Facet chips */}
      {facetVocab.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
          {(tagsExpanded ? facetVocab : facetVocab.slice(0, TAGS_VISIBLE)).map((t) => {
            const active = activeFacets.has(t)
            return (
              <button
                key={t}
                onClick={() => toggleFacet(t)}
                className={`ze-chip${active ? ' ze-chip--active' : ''}`}
                aria-pressed={active}
              >
                {t}
              </button>
            )
          })}
          {!tagsExpanded && facetVocab.length > TAGS_VISIBLE && (
            <button
              onClick={() => setTagsExpanded(true)}
              className="ze-chip"
              title={`Show ${facetVocab.length - TAGS_VISIBLE} more filters`}
            >
              …
            </button>
          )}
          {tagsExpanded && (
            <button
              onClick={() => setTagsExpanded(false)}
              className="ze-chip"
            >
              less
            </button>
          )}
        </div>
      )}

      {/* Count line */}
      <div
        style={{
          fontFamily: 'var(--card-sans)',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--grey)',
          marginBottom: 6,
        }}
      >
        {filtered.length} {filtered.length === 1 ? 'collection' : 'collections'}
      </div>

      {/* Rows */}
      <div style={{ marginLeft: -14, marginRight: -14 }}>
        {filtered.map((id) => {
          const config = DATASET_MAP[id]
          if (!config) return null
          const isActive = id === datasetId
          const meta = (config.tags ?? []).join(' · ')
          return (
            <div
              key={id}
              onClick={() => setDatasetId(id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setDatasetId(id)
                }
              }}
              title={config.sourceInfo || undefined}
              style={{
                padding: isActive ? '10px 12px 10px 11px' : '8px 14px',
                cursor: 'pointer',
                background: isActive ? 'var(--paper)' : 'transparent',
                borderLeft: isActive
                  ? '3px solid var(--teal)'
                  : '3px solid transparent',
                borderTop: isActive
                  ? '1px solid var(--rule)'
                  : '1px solid transparent',
                borderBottom: '1px solid var(--rule)',
                boxShadow: isActive
                  ? '0 1px 2px rgba(0,0,0,0.04)'
                  : 'none',
                transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  ;(e.currentTarget as HTMLDivElement).style.background =
                    'var(--paper)'
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  ;(e.currentTarget as HTMLDivElement).style.background =
                    'transparent'
                }
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--card-sans)',
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'var(--navy)',
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {config.info}
              </div>
              {meta && (
                <div
                  style={{
                    fontFamily: 'var(--card-mono)',
                    fontSize: 10,
                    color: 'var(--slate)',
                    marginTop: 2,
                    lineHeight: 1.35,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {meta}
                </div>
              )}
            </div>
          )
        })}

        {filtered.length === 0 && (
          <div
            style={{
              padding: '12px 14px',
              fontSize: 11,
              color: 'var(--grey)',
              fontFamily: 'var(--card-sans)',
            }}
          >
            No matches.
          </div>
        )}
      </div>
    </div>
  )
}

export default DatasetBrowser
